import React, { useCallback, useEffect, useState } from 'react';
import { FolderOpen } from 'lucide-react';
import { btnStyle } from './theme';
import DriveUploadJobList from './DriveUploadJobList';

// One-way "upload to Google Drive" destination -- NOT Companion's full
// two-way Drive Sync (mappings, watchers, conflict resolution). Just: pick
// or create a destination folder, upload this ingest's verified files into
// it preserving their relative subfolder structure. See electron/
// drive-upload.js's own docstring for the full reasoning.
export default function DriveUploadSection({ c, manifest }) {
  const [authStatus, setAuthStatus] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [folders, setFolders] = useState([]);
  const [folderPath, setFolderPath] = useState([]); // [{id, name}] breadcrumb -- empty = Drive root
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [driveJobs, setDriveJobs] = useState(null);
  const [driveError, setDriveError] = useState(null);

  const currentFolderId = folderPath.length ? folderPath[folderPath.length - 1].id : null;

  const loadAuthStatus = useCallback(async () => {
    const result = await window.electronAPI.driveUpload.getAuthStatus();
    if (result?.success) setAuthStatus(result.data);
  }, []);
  useEffect(() => { loadAuthStatus(); }, [loadAuthStatus]);

  const loadFolders = useCallback(async () => {
    const result = await window.electronAPI.driveUpload.listFolders(currentFolderId);
    if (result?.success) setFolders(result.data);
  }, [currentFolderId]);
  useEffect(() => { if (authStatus?.connected) loadFolders(); }, [authStatus, loadFolders]);

  const connect = useCallback(async () => {
    setConnecting(true);
    setDriveError(null);
    const result = await window.electronAPI.driveUpload.connect();
    setConnecting(false);
    if (result?.success) setAuthStatus(result.data);
    else setDriveError(result?.error || 'Failed to connect Google Drive');
  }, []);

  const createFolder = useCallback(async () => {
    if (!newFolderName.trim()) return;
    setCreatingFolder(true);
    setDriveError(null);
    const result = await window.electronAPI.driveUpload.createFolder(currentFolderId, newFolderName.trim());
    setCreatingFolder(false);
    if (result?.success) {
      setNewFolderName('');
      loadFolders();
    } else {
      setDriveError(result?.error || 'Failed to create folder');
    }
  }, [currentFolderId, newFolderName, loadFolders]);

  const startUpload = useCallback(async () => {
    setDriveError(null);
    const verifiedFiles = manifest.files.filter(f => f.verified).map(f => ({ relativePath: f.relativePath, size: f.size }));
    const destFolderName = folderPath.length ? folderPath[folderPath.length - 1].name : 'My Drive';
    const result = await window.electronAPI.driveUpload.enqueue(verifiedFiles, manifest.destPath, currentFolderId, destFolderName);
    if (result?.success) setDriveJobs(result.data);
    else setDriveError(result?.error || 'Failed to start Drive upload');
  }, [manifest, currentFolderId, folderPath]);

  useEffect(() => {
    if (!driveJobs) return;
    const offStarted = window.electronAPI.driveUpload.onJobStarted((job) => {
      setDriveJobs((prev) => prev && prev.map(j => (j.id === job.id ? job : j)));
    });
    const offProgress = window.electronAPI.driveUpload.onProgress(({ id, bytesDone }) => {
      setDriveJobs((prev) => prev && prev.map(j => (j.id === id ? { ...j, bytesDone } : j)));
    });
    const offComplete = window.electronAPI.driveUpload.onJobComplete((job) => {
      setDriveJobs((prev) => prev && prev.map(j => (j.id === job.id ? job : j)));
    });
    return () => { offStarted(); offProgress(); offComplete(); };
  }, [driveJobs]);

  if (!authStatus) return null; // still loading

  const driveDone = driveJobs ? driveJobs.filter(j => j.status === 'completed' || j.status === 'failed' || j.status === 'cancelled').length : 0;
  const driveFailed = driveJobs ? driveJobs.filter(j => j.status === 'failed').length : 0;

  return (
    <div style={{ padding: '12px 14px', border: `1px solid ${c.rule}`, borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <div style={{ fontSize: '12px', fontWeight: 600 }}>Google Drive</div>

      {!authStatus.connected ? (
        <>
          <div style={{ fontSize: '11px', color: c.muted }}>
            {authStatus.action === 'sign_in_required'
              ? 'Sign in to tarr.ink to connect Google Drive.'
              : 'Connect your Google account to upload these files to Drive.'}
          </div>
          <button onClick={connect} disabled={connecting} style={{ ...btnStyle(c, 'primary'), alignSelf: 'flex-start' }}>
            {connecting ? 'Connecting…' : 'Connect Google Drive'}
          </button>
        </>
      ) : !driveJobs ? (
        <>
          <div style={{ fontSize: '11px', color: c.muted }}>
            Destination: {folderPath.length ? folderPath.map(f => f.name).join(' / ') : 'My Drive (root)'}
          </div>
          {folderPath.length > 0 && (
            <button onClick={() => setFolderPath((p) => p.slice(0, -1))} style={{ ...btnStyle(c, 'ghost'), alignSelf: 'flex-start' }}>
              ← Back
            </button>
          )}
          {folders.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '140px', overflow: 'auto' }}>
              {folders.map(folder => (
                <button
                  key={folder.id}
                  onClick={() => setFolderPath((p) => [...p, folder])}
                  style={{ ...btnStyle(c, 'ghost'), justifyContent: 'flex-start' }}
                >
                  <FolderOpen size={13} /> {folder.name}
                </button>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="New folder name"
              style={{ flex: 1, padding: '6px 9px', fontSize: '11.5px', borderRadius: '6px', border: `1px solid ${c.rule}`, background: c.inputBg, color: c.text }}
            />
            <button onClick={createFolder} disabled={creatingFolder || !newFolderName.trim()} style={btnStyle(c, 'default')}>
              {creatingFolder ? 'Creating…' : 'Create'}
            </button>
          </div>
          <button onClick={startUpload} style={{ ...btnStyle(c, 'primary'), alignSelf: 'flex-start' }}>
            Upload to Google Drive
          </button>
        </>
      ) : (
        <>
          <div style={{ height: '8px', borderRadius: '4px', background: c.rule, overflow: 'hidden' }}>
            <div style={{
              height: '100%', background: c.accent, transition: 'width 0.2s',
              width: `${driveJobs.length ? Math.round((driveDone / driveJobs.length) * 100) : 0}%`,
            }} />
          </div>
          <div style={{ fontSize: '11px', color: c.muted }}>
            {driveDone}/{driveJobs.length} uploaded{driveFailed > 0 ? ` · ${driveFailed} failed` : ''}
          </div>
          <DriveUploadJobList c={c} jobs={driveJobs} />
        </>
      )}
      {driveError && <div style={{ fontSize: '11px', color: c.err }}>{driveError}</div>}
    </div>
  );
}
